if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.swipeBelowDesktop = this.hasAttribute('data-swipe-below-desktop');
        this.mql = window.matchMedia(this.swipeBelowDesktop ? '(min-width: 990px)' : '(min-width: 750px)');
        if (this.swipeBelowDesktop) {
          this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
          this.onLayoutChangeBound = this.onLayoutChange.bind(this);
          this.onLayoutChange();
        }
        if (!this.elements.thumbnails) return;

        if (!this.swipeBelowDesktop) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.swipeBelowDesktop) this.mql.addEventListener('change', this.onLayoutChangeBound);
      }

      disconnectedCallback() {
        if (this.swipeBelowDesktop) this.mql.removeEventListener('change', this.onLayoutChangeBound);
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        if (!activeMedia) return;
        if (this.swipeBelowDesktop && !this.mql.matches) {
          const changed = !activeMedia.classList.contains('is-active');
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
            item.classList.toggle('is-active', item === activeMedia);
          });
          if (changed) {
            this.playActiveMedia(activeMedia);
            const position = Array.from(this.elements.viewer.sliderItems).indexOf(activeMedia) + 1;
            this.announceLiveRegion(activeMedia, position);
          }
        }
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onLayoutChange() {
        const viewer = this.elements.viewer;
        if (!viewer.slider) return;
        if (this.mql.matches && this.dataset.desktopLayout.includes('thumbnail')) {
          this.removeListSemantic();
        } else {
          viewer.slider.setAttribute('role', 'list');
          viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'listitem'));
        }
        requestAnimationFrame(() => {
          const active = viewer.querySelector('.is-active[data-media-id]');
          viewer.slider.scrollTo({ left: !this.mql.matches && active ? active.offsetLeft : 0, behavior: 'instant' });
          viewer.resetPages();
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider && (!this.swipeBelowDesktop || this.mql.matches)) {
            this.elements.viewer.resetPages();
          }
        }

        if (this.swipeBelowDesktop && !this.mql.matches && this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
          this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.swipeBelowDesktop && !this.mql.matches ? 'instant' : 'auto',
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
